import styled from '@emotion/styled';
import PhoneLine from '@icons/phone/PhoneLine';
import { PaymentMethod } from '@models/order';
import { ReactNode } from 'react';

import cardIcon from '../imgs/ic_card.svg';
import flashIcon from '../imgs/ic_flash.svg';
import prepayIcon from '../imgs/ic_prepay.svg';
import appleBg from '../imgs/logo_apple_bg.svg';
import appleFrame from '../imgs/logo_apple_frame.svg';
import appleMark from '../imgs/logo_apple_mark.svg';
import applePay from '../imgs/logo_apple_pay.svg';
import hanaLogo from '../imgs/logo_hana.png';
import kakaoLogo from '../imgs/logo_kakao.svg';
import kbLogo from '../imgs/logo_kb.png';
import naverLogo from '../imgs/logo_naver.svg';
import paycoLogo from '../imgs/logo_payco.svg';
import tossLogo from '../imgs/logo_toss.svg';
import phoneIcon from '../imgs/phone.png';
import wooriLogo from '../imgs/woori.png';

interface PaymentMethodInfo {
    /** order 네임스페이스의 결제수단 이름 키 */
    nameKey: `paymentMethod.list.${PaymentMethod}`;
    icon: ReactNode;
}

/** 30x30 정사각 로고 */
function SquareLogo({ src, rounded }: { src: string, rounded?: boolean }) {
    return (
        <LogoBox>
            <SquareImage src={src} alt='' data-rounded={rounded} />
        </LogoBox>
    );
}

const LogoBox = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    width: 38px;
`;

const SquareImage = styled.img`
    height: 30px;
    width: 30px;

    &[data-rounded="true"] {
        border-radius: 8px;
    }
`;

const IconBox = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    height: 24px;
    justify-content: center;
    width: 24px;
`;

/** 38x16 알약 모양 로고 */
const Pill = styled.div`
    border-radius: 12px;
    flex-shrink: 0;
    height: 16px;
    overflow: hidden;
    position: relative;
    width: 38px;

    > img {
        position: absolute;
    }
`;

const AppleMark = styled.div`
    flex-shrink: 0;
    height: 20px;
    position: relative;
    width: 30px;

    > img {
        position: absolute;
    }
`;

const cardNode = (
    <IconBox>
        <img src={cardIcon} alt='' width={24} height={24} />
    </IconBox>
);

const tossNode = (
    <Pill style={{ backgroundColor: '#ffffff' }}>
        <img src={tossLogo} alt='' width={34} height={8.81} style={{ left: 2, top: 3.48 }} />
    </Pill>
);

/** 결제수단별 이름과 아이콘 */
const PAYMENT_METHODS: Record<PaymentMethod, PaymentMethodInfo> = {
    CREDIT_CARD: {
        nameKey: 'paymentMethod.list.CREDIT_CARD',
        icon: cardNode,
    },
    EASY_CARD: {
        nameKey: 'paymentMethod.list.EASY_CARD',
        icon: (
            <IconBox>
                <img src={flashIcon} alt='' width={20} height={22.66} />
            </IconBox>
        ),
    },
    NAVER_PAY: {
        nameKey: 'paymentMethod.list.NAVER_PAY',
        icon: (
            <Pill>
                <img src={naverLogo} alt='' width={38} height={38} style={{ left: 0, top: -11 }} />
            </Pill>
        ),
    },
    KAKAO_PAY: {
        nameKey: 'paymentMethod.list.KAKAO_PAY',
        icon: (
            <Pill style={{ backgroundColor: '#ffec00' }}>
                <img src={kakaoLogo} alt='' width={38} height={19} style={{ left: 0, top: 0 }} />
            </Pill>
        ),
    },
    TOSS_PAY: {
        nameKey: 'paymentMethod.list.TOSS_PAY',
        icon: tossNode,
    },
    TOSS_PAY_DIRECT: {
        nameKey: 'paymentMethod.list.TOSS_PAY_DIRECT',
        icon: tossNode,
    },
    APPLE_PAY: {
        nameKey: 'paymentMethod.list.APPLE_PAY',
        icon: (
            <LogoBox>
                <AppleMark>
                    <img src={appleBg} alt='' style={{ inset: 0, width: '100%', height: '100%' }} />
                    <img src={appleFrame} alt='' style={{ left: '2.13%', top: '3.33%' }} />
                    <img src={appleMark} alt='' style={{ left: '13.55%', top: '27.42%' }} />
                    <img src={applePay} alt='' style={{ left: '37.78%', top: '30.15%' }} />
                </AppleMark>
            </LogoBox>
        ),
    },
    KB_PAY: {
        nameKey: 'paymentMethod.list.KB_PAY',
        icon: <SquareLogo src={kbLogo} rounded />,
    },
    PAYCO: {
        nameKey: 'paymentMethod.list.PAYCO',
        icon: <SquareLogo src={paycoLogo} />,
    },
    HANA_PAY: {
        nameKey: 'paymentMethod.list.HANA_PAY',
        icon: <SquareLogo src={hanaLogo} rounded />,
    },
    WOORI_PAY: {
        nameKey: 'paymentMethod.list.WOORI_PAY',
        icon: <SquareLogo src={wooriLogo} />,
    },
    MOBILE_PAYMENT: {
        nameKey: 'paymentMethod.list.MOBILE_PAYMENT',
        icon: <PhoneLine size={30}/>,
    },
    PREPAID_CARD: {
        nameKey: 'paymentMethod.list.PREPAID_CARD',
        icon: <SquareLogo src={prepayIcon} />,
    },
    GIFT: {
        nameKey: 'paymentMethod.list.GIFT',
        icon: cardNode,
    },
};

export default PAYMENT_METHODS;
